import { APIExtRoutes } from "@constants";
import { APIResponse } from "@models/core";
import { PlatformForFilterDTO } from "@models/managed-accounts/entities/filter";
import {
  InvestmentQueryOptions,
  InvestmentSelectionDTO,
} from "@models/managed-accounts/entities/step/setup";
import {
  FilterCalculatedInvestmentDTO,
  SmaListInvestmentDto,
} from "@pages/managed-accounts/sma-list/components/tables/model";
import { APIGatewayService, handleErrorProxy } from "@services/shService";
import { generatePath } from "react-router";
import { SMAListExcelExcelRequestDTO } from "@models/managed-accounts/entities/smaList";
import {
  PortfolioCalculatedReturnsDTO,
  ProcessFileRequest,
} from "@models/product/investment-product/entities/investmentProduct";

export async function getInvestmentSelections(
  queryOptions: InvestmentQueryOptions,
): Promise<APIResponse<InvestmentSelectionDTO[]>> {
  const response = await APIGatewayService.get<InvestmentSelectionDTO[]>(
    generatePath(APIExtRoutes.investmentsSelections),
    { params: queryOptions },
  );

  return handleErrorProxy(response);
}

export async function getInvestmentFavourited(
  queryOptions: InvestmentQueryOptions,
): Promise<APIResponse<InvestmentSelectionDTO[]>> {
  const response = await APIGatewayService.get<InvestmentSelectionDTO[]>(
    generatePath(APIExtRoutes.investmentsFavourited),
    { params: queryOptions },
  );

  return handleErrorProxy(response);
}

export async function putInvestmentFavourited(
  shCodes: string[],
  investmentProductId = "",
): Promise<APIResponse<string>> {
  const apiPath =
    generatePath(APIExtRoutes.investmentsFavourited) +
    (investmentProductId !== ""
      ? `?investmentProductId=${investmentProductId}`
      : "");
  const response = await APIGatewayService.put<string>(
    generatePath(apiPath),
    shCodes,
  );

  return handleErrorProxy(response);
}

export async function getInvestmentFiltersPlatforms(
  investmentProductId = "",
): Promise<APIResponse<PlatformForFilterDTO[]>> {
  const platformPath =
    APIExtRoutes.investmentsFiltersPlatforms +
    (investmentProductId !== ""
      ? `?investmentProductId=${investmentProductId}`
      : "");
  const response = await APIGatewayService.get<PlatformForFilterDTO[]>(
    platformPath,
  );

  return handleErrorProxy(response);
}

export async function getSmaList(): Promise<APIResponse<SmaListInvestmentDto>> {
  const response = await APIGatewayService.get<SmaListInvestmentDto>(
    generatePath(APIExtRoutes.investmentsSmaList),
  );

  return handleErrorProxy(response);
}

export async function updateSmaList(
  filterCalculatedInvestmentDTO: FilterCalculatedInvestmentDTO,
  signal?: AbortSignal,
): Promise<APIResponse<SmaListInvestmentDto>> {
  const response = await APIGatewayService.put<SmaListInvestmentDto>(
    generatePath(APIExtRoutes.investmentsSmaList),
    filterCalculatedInvestmentDTO,
    { signal: signal },
  );

  return handleErrorProxy(response);
}

export async function exportExcelSmaList(
  smaListExcelRequestDTO: SMAListExcelExcelRequestDTO,
) {
  const response = await APIGatewayService.post<SmaListInvestmentDto>(
    generatePath(APIExtRoutes.investmentsSmaListExportExcel),
    smaListExcelRequestDTO,
  );

  return handleErrorProxy(response);
}

export const autoCalculatePerformance = async (
  productId: string,
  modelId: string,
  fileUrl: string,
  excludedMetrics: string[],
): Promise<APIResponse<PortfolioCalculatedReturnsDTO>> => {
  const response = await APIGatewayService.post<PortfolioCalculatedReturnsDTO>(
    generatePath(APIExtRoutes.calculatePerformance, {
      modelId,
      productId,
    }),
    {
      fileUrl: fileUrl,
      excludedMetrics: excludedMetrics,
    },
  );
  return handleErrorProxy(response);
};

export const downloadDocument = async (
  productId: string | undefined,
  modelId: string,
  fileRequest: ProcessFileRequest,
): Promise<APIResponse<Blob>> => {
  const response = await APIGatewayService.post<Blob>(
    generatePath(APIExtRoutes.downloadPerformanceFile, {
      productId,
      modelId,
    }),
    fileRequest,
    {
      responseType: "blob",
    },
  );

  return handleErrorProxy(response);
};

export const reCalculatePerformance = async (
  productId: string | undefined,
  modelId: string,
  endDate: Date,
): Promise<APIResponse<PortfolioCalculatedReturnsDTO>> => {
  const response = await APIGatewayService.post<PortfolioCalculatedReturnsDTO>(
    generatePath(APIExtRoutes.reCalculatePerformance, {
      modelId,
      productId,
    }),
    {
      endDate,
    },
  );
  return handleErrorProxy(response);
};

export const getPlatformAnalysisInvestmentsBySubProductId = async ({
  platformAnalysisId,
  subProductId,
}: {
  platformAnalysisId: string;
  subProductId: string;
}): Promise<APIResponse<InvestmentSelectionDTO[]>> => {
  const response = await APIGatewayService.get<InvestmentSelectionDTO[]>(
    generatePath(APIExtRoutes.platformAnalysisInvestmentsBySubProductId, {
      platformAnalysisId,
      subProductId,
    }),
  );

  return handleErrorProxy(response);
};
